export interface ZoneColorOption {
  hex: string;
  label: string;
}

/**
 * The eight colors an operator can assign to a zone.
 *
 * The first four are the type defaults, so the default always highlights a real
 * swatch. The rest exist to tell two zones of the SAME type apart.
 *
 * A shade already in service elsewhere on the display is a hazard here, not a
 * reassurance: it lets operator-drawn geometry impersonate something the map
 * already uses that colour to mean. Two entries did exactly that — Blue was
 * `#3B82F6` at 0.1 opacity, byte-identical to `airspace-d-fill`, and Purple was
 * `#A855F7`, the Class C fill. Geofences are added AFTER the airspace layers,
 * so a blue zone drawn over Class D stacked to one continuous region. On a
 * product whose claim is a unified air picture, a drawn advisory that reads as
 * regulatory airspace is a correctness problem. Both are shifted clear.
 */
export const ZONE_COLORS: readonly ZoneColorOption[] = [
  { hex: '#22C55E', label: 'Green' },
  { hex: '#EF4444', label: 'Red' },
  { hex: '#F59E0B', label: 'Amber' },
  { hex: '#2DD4BF', label: 'Teal' },
  { hex: '#06B6D4', label: 'Cyan' },
  { hex: '#E879F9', label: 'Magenta' },
  { hex: '#F97316', label: 'Orange' },
  { hex: '#94A3B8', label: 'Slate' },
];

/**
 * The color each zone type falls back to when the operator hasn't picked one.
 * Every value here must also appear in ZONE_COLORS, so the default always
 * highlights a real swatch rather than showing an unselected row.
 */
export const ZONE_TYPE_HEX: Record<string, string> = {
  operational: '#22C55E',
  restricted: '#EF4444',
  warning: '#F59E0B',
  jamming: '#EF4444',
};

/** Fallback for a zone type with no mapping of its own. */
export const DEFAULT_ZONE_COLOR = '#22C55E';

/**
 * The color a zone will actually be saved and rendered with: the operator's
 * explicit pick, or — when they haven't made one — the zone type's default.
 */
export function resolveZoneColor(type: string, picked: string | null): string {
  return picked ?? ZONE_TYPE_HEX[type] ?? DEFAULT_ZONE_COLOR;
}
